import { Injectable } from '@angular/core'
import { Apollo, QueryRef } from 'apollo-angular'
import gql from 'graphql-tag'
import { DocumentNode } from 'graphql'

type UserKickstartCodes =  'video_tour'
type CustomerKickstartCodes = 'ten_topics' | 'account_setup' | 'content_strategy'

export interface ExtraPurchase {
  id: number;
  purchasedAt: Date;
  extra: {
    id: number;
    code: UserKickstartCodes | CustomerKickstartCodes
  }
}

export interface Data {
  currentUser: User;
}

export interface User {
  id: number;
  userableId: number;
  email: string;
  role: string;
  memberAdmin: boolean;
  adminImpersonating: boolean;
  writer: Writer;
  account: Account;
  extraPurchases: ExtraPurchase[]
  demoScheduledFor: Date;
  fullName: string;

  // Abilities
  canViewWhiteLabelProfile: boolean;
  canShowBillingAccount: boolean
  canIndexAccountInvoices: boolean
  canUpdateMembers: boolean
  canUpdateUser: boolean
  canEditUserEmailPreferences: boolean
  canChatHelp: boolean
  canPhoneHelp: boolean
  canResetDemo: boolean
  canIndexHelpPage: boolean
  canCreateWriterGoal: boolean
}

export interface Writer {
  id: number;
  noKeywordsAlert: boolean;
  proposedKeywordsNotificationsCount: number;
  assignmentCount: number;
  forumNotificationsCount: number;
  canReadDiscussion: boolean;
  canEditProfile: boolean;
  canEdit: boolean
  hasW9Record: boolean;
  customerSearches: {
    name: string,
    url: string
  }[];
  state: string
  hasSetGoalsForThisWeek: boolean
  commitment: boolean
}

export interface Account {
  id: number;
  customersCount: number;
  agency: boolean;
  monthlyRevenue: number;
  setupAt: Date;
  showWhiteLabel: boolean;
  currentCustomers: {
    id: number
    extraPurchases: ExtraPurchase[]
  }
  whiteLabelProfile: WhiteLabelProfile;
}

export interface WhiteLabelProfile {
  id: number
  name: string
  displayName: boolean
  logo: string
}

const extraPurchaseFragment = gql`
  fragment NavBarExtraPurchaseFragment on ExtraPurchase {
    id
    purchasedAt
    extra {
      id
      code
    }
  }
`

const query: DocumentNode = gql`
  query NavBarQuery {
    currentUser {
      id
      userableId
      email
      role
      adminImpersonating
      memberAdmin
      fullName
      writer {
        id
        noKeywordsAlert
        canReadDiscussion
        canEditProfile
        proposedKeywordsNotificationsCount
        assignmentCount
        forumNotificationsCount
        hasW9Record
        canUpdate: can(action: "update")
        customerSearches {
          name
          url
        }
        state
        hasSetGoalsForThisWeek
        commitment
      }
      account {
        id
        customersCount
        agency
        monthlyRevenue
        setupAt
        showWhiteLabel
        currentCustomers {
          id
          extraPurchases {
            ...NavBarExtraPurchaseFragment
          }
        }
        whiteLabelProfile {
          id
          name
          logo
          displayName
        }
      }
      extraPurchases {
        ...NavBarExtraPurchaseFragment
      }
      demoScheduledFor
      canViewWhiteLabelProfile: can(action: "view", subject: "white_label_profile")
      canShowBillingAccount: can(action: "show", subject: "billing_account")
      canIndexAccountInvoices: can(action: "index", subject: "account_invoice")
      canUpdateMembers: can(action: "update", subject: "member")
      canUpdateUser: can(action: "update", subject: "User")
      canEditUserEmailPreferences: can(action: "edit_email_preferences", subject: "User")
      canChatHelp: can(action: "chat", subject: "help")
      canPhoneHelp: can(action: "phone", subject: "help")
      canResetDemo: can(action: "setup", subject: "demo_setup")
      canIndexHelpPage: can(action: "index", subject: "HelpPage")
      canCreateWriterGoal: can(action: "create", subject: "WriterGoal")
    }
  }
  ${extraPurchaseFragment}
`

@Injectable({
  providedIn: 'root'
})
export class NavBarQueries {
  constructor(private apollo: Apollo) {}

  mainQuery(): QueryRef<Data> {
    return this.apollo.watchQuery<Data>({
      query,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    })
  }
}
